import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Plan {
    code: string;
    name_he: string;
    price_monthly_nis: number;
    daily_recipe_limit: number | null;
    monthly_image_credits: number | null;
    max_saved_recipes: number | null;
    can_customize_categories: boolean;
    can_regenerate_image: boolean;
    can_personal_chefs: boolean;
    personal_chefs_limit: number;
    is_unlimited: boolean;
}

export interface UsageSnapshot {
    todayRecipes: number;
    monthImages: number;
    savedRecipes: number;
}

interface SubscriptionContextValue {
    loading: boolean;
    plan: Plan | null;
    usage: UsageSnapshot;
    refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
    loading: false,
    plan: null,
    usage: { todayRecipes: 0, monthImages: 0, savedRecipes: 0 },
    refresh: async () => { },
});

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [plan, setPlan] = useState<Plan | null>(null);
    const [usage, setUsage] = useState<UsageSnapshot>({ todayRecipes: 0, monthImages: 0, savedRecipes: 0 });

    const load = async () => {
        if (!user) {
            setPlan(null);
            setUsage({ todayRecipes: 0, monthImages: 0, savedRecipes: 0 });
            return;
        }
        setLoading(true);
        try {
            const today = new Date();
            const dateStr = today.toISOString().slice(0, 10);
            let planData: any = null;
            try {
                const { data, error } = await supabase.rpc('get_effective_plan', { p_user: user.id });
                if (!error) planData = data;
            } catch { }

            let dailyCountVal = 0;
            try {
                const { data, error } = await supabase.rpc('get_daily_recipe_count', { p_user: user.id, p_day: dateStr });
                if (!error && typeof data === 'number') dailyCountVal = data;
            } catch { }

            let monthlyImagesVal = 0;
            try {
                const { data, error } = await supabase.rpc('get_monthly_image_count', { p_user: user.id, p_month: dateStr });
                if (!error && typeof data === 'number') monthlyImagesVal = data;
            } catch { }

            let savedCountVal = 0;
            try {
                const { count, error } = await supabase
                    .from('saved_recipes')
                    .select('id', { count: 'exact', head: true })
                    .eq('user_id', user.id);
                if (!error && typeof count === 'number') savedCountVal = count;
            } catch { }

            setPlan(planData as Plan | null);
            setUsage({
                todayRecipes: dailyCountVal,
                monthImages: monthlyImagesVal,
                savedRecipes: savedCountVal,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // refresh on auth/user change
    }, [user?.id]);

    const value = useMemo(() => ({ loading, plan, usage, refresh: load }), [loading, plan, usage]);
    return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = () => useContext(SubscriptionContext);


